// src/pages/HomePage.jsx
import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import ProductCard from '../components/ProductCard';
import products from '../data/products';
import './HomePage.css';

const HomePage = () => {
  const navigate = useNavigate();
  const featuredProducts = products.filter(p => p.isNew || p.popular).slice(0, 8);
  const categories = [...new Set(products.map(p => p.category))];
  const [currentSlide, setCurrentSlide] = useState(0);
  const [isAutoPlaying, setIsAutoPlaying] = useState(true);
  const [windowWidth, setWindowWidth] = useState(typeof window !== 'undefined' ? window.innerWidth : 1200);
  const carouselRef = useRef(null);
  const intervalRef = useRef(null);

  const categoryIcons = {
    'Flower': '🌿',
    'Exotic-Flower': '🌸',
    'Edibles': '🍬',
    'Concentrates': '⚗️',
    'cart': '🔋',
    'disposable-cart': '💨',
    'Pre-rolls': '🚬',
    'Shake': '🍃'
  };

  const categoryColors = {
    'Flower': '#4CAF50',
    'Exotic-Flower': '#9C27B0',
    'Edibles': '#FF9800',
    'Concentrates': '#673AB7',
    'cart': '#2196F3',
    'disposable-cart': '#00BCD4',
    'Pre-rolls': '#F44336',
    'Shake': '#795548'
  };

  // Handle window resize
  useEffect(() => {
    const handleResize = () => {
      setWindowWidth(window.innerWidth);
    };
    
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Scroll to top on mount
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  // Smooth navigation with scroll to top
  const handleNavigation = useCallback((path) => {
    navigate(path);
    window.scrollTo({
      top: 0,
      behavior: 'smooth'
    });
  }, [navigate]);

  // Get slides per view based on screen width
  const getSlidesPerView = useCallback(() => {
    if (windowWidth >= 1200) return 4;
    if (windowWidth >= 768) return 3;
    if (windowWidth >= 480) return 2;
    return 1;
  }, [windowWidth]);

  const slidesPerView = getSlidesPerView();
  const totalSlides = Math.ceil(categories.length / slidesPerView);

  // Carousel navigation
  const nextSlide = useCallback(() => {
    setCurrentSlide(prev => (prev + 1) % totalSlides);
  }, [totalSlides]);

  const prevSlide = useCallback(() => {
    setCurrentSlide(prev => (prev - 1 + totalSlides) % totalSlides);
  }, [totalSlides]);

  const goToSlide = (index) => {
    setCurrentSlide(index);
    setIsAutoPlaying(false);
    setTimeout(() => setIsAutoPlaying(true), 5000);
  };

  // Auto-play carousel
  useEffect(() => {
    if (isAutoPlaying && totalSlides > 1) {
      intervalRef.current = setInterval(nextSlide, 5000);
    }
    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }
    };
  }, [isAutoPlaying, nextSlide, totalSlides]);

  const handleMouseEnter = () => {
    setIsAutoPlaying(false);
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
    }
  };

  const handleMouseLeave = () => {
    setIsAutoPlaying(true);
  };

  const getCategoryStats = (category) => {
    const count = products.filter(p => p.category === category).length;
    const avgPrice = products
      .filter(p => p.category === category)
      .reduce((sum, p) => sum + (p.price || 0), 0) / count;
    
    return { count, avgPrice: Math.round(avgPrice) };
  };

  // Hero background style
  const heroStyle = {
    backgroundImage: 'linear-gradient(rgba(0, 0, 0, 0.5), rgba(0, 0, 0, 0.5)), url("/images/heros.PNG")',
    backgroundSize: 'cover',
    backgroundPosition: 'center',
    backgroundRepeat: 'no-repeat',
    position: 'relative'
  };

  return (
    <div className="home-page">
      {/* Hero Section - Modern Responsive */}
      <section className="hero-section" style={heroStyle}>
        <div className="container">
          <div className="hero-content">
            <div className="hero-badge">
              <span className="badge-icon">📍</span>
              Washington DC's Trusted Source
            </div>
            
            <h1 className="hero-title">
              Premium Cannabis
              <span className="title-accent">Delivery</span>
            </h1>
            
            <p className="hero-subtitle">
              Experience the finest selection of lab-tested cannabis products.
              Fast, discreet, and professional delivery right to your door.
            </p>
            
            <div className="hero-actions">
              <button
                onClick={() => handleNavigation('/products')}
                className="btn btn-outline-light btn-lg"
              >
                <span className="btn-icon">🛍️</span>
                Shop All Products
              </button>
              
              <button
                onClick={() => handleNavigation('/categories')}
                className="btn btn-primary btn-lg"
              >
                <span className="btn-icon">📍</span>
                Browse Categories
              </button>
            </div>

            <div className="hero-stats">
              <div className="stat-item">
                <span className="stat-number">100+</span>
                <span className="stat-label">Products</span>
              </div>
              <div className="stat-item">
                <span className="stat-number">24/7</span>
                <span className="stat-label">Delivery</span>
              </div>
              <div className="stat-item">
                <span className="stat-number">500+</span>
                <span className="stat-label">Happy Clients</span>
              </div>
            </div>
          </div>
        </div>

        <div className="hero-wave">
          <svg viewBox="0 0 1440 120" fill="none">
            <path d="M0,64L80,69.3C160,75,320,85,480,80C640,75,800,53,960,48C1120,43,1280,53,1360,58.7L1440,64L1440,120L1360,120C1280,120,1120,120,960,120C800,120,640,120,480,120C320,120,160,120,80,120L0,120Z" 
              fill="var(--background)"/>
          </svg>
        </div>
      </section>

      {/* Featured Products Section */}
      <section className="section featured-section">
        <div className="container">
          <div className="section-header">
            <div className="header-content">
              <span className="section-tag">Featured</span>
              <h2 className="section-title">Popular Products</h2>
              <p className="section-subtitle">Discover our most loved items</p>
            </div>
            
            <button
              onClick={() => handleNavigation('/products')}
              className="btn btn-outline view-all-btn"
            >
              <span>View All</span>
              <svg className="arrow-icon" viewBox="0 0 24 24" fill="none">
                <path d="M5 12h14M12 5l7 7-7 7" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
              </svg>
            </button>
          </div>

          <div className="products-grid">
            {featuredProducts.slice(0, 8).map(product => (
              <ProductCard key={product.id} product={product} />
            ))}
          </div>
        </div>
      </section>

      {/* Category Carousel Section */}
      <section className="section carousel-section">
        <div className="container">
          <div className="section-header">
            <div className="header-content">
              <span className="section-tag">Categories</span>
              <h2 className="section-title">Shop by Category</h2>
              <p className="section-subtitle">Explore our curated collections</p>
            </div>

            {totalSlides > 1 && (
              <div className="carousel-controls">
                <button
                  className="carousel-btn prev"
                  onClick={prevSlide}
                  aria-label="Previous categories"
                  disabled={currentSlide === 0}
                >
                  <svg viewBox="0 0 24 24" fill="none">
                    <path d="M15 18l-6-6 6-6" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
                  </svg>
                </button>
                
                <button
                  className="carousel-btn next"
                  onClick={nextSlide}
                  aria-label="Next categories"
                  disabled={currentSlide === totalSlides - 1}
                >
                  <svg viewBox="0 0 24 24" fill="none">
                    <path d="M9 18l6-6-6-6" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
                  </svg>
                </button>
              </div>
            )}
          </div>

          <div
            className="category-carousel-container"
            ref={carouselRef}
            onMouseEnter={handleMouseEnter}
            onMouseLeave={handleMouseLeave}
          >
            <div
              className="carousel-track"
              style={{
                transform: `translateX(-${currentSlide * 100}%)`,
                transition: 'transform 0.5s cubic-bezier(0.4, 0, 0.2, 1)'
              }}
            >
              {Array.from({ length: totalSlides }).map((_, slideIndex) => (
                <div key={slideIndex} className="carousel-slide">
                  {categories
                    .slice(slideIndex * slidesPerView, (slideIndex + 1) * slidesPerView)
                    .map(category => {
                      const stats = getCategoryStats(category);
                      const icon = categoryIcons[category] || '📦';
                      const color = categoryColors[category] || 'var(--primary)';

                      return (
                        <button
                          key={category}
                          onClick={() => handleNavigation(`/category/${category}`)}
                          className="category-card"
                          style={{ '--category-color': color }}
                        >
                          <div className="card-inner">
                            <div className="card-front">
                              <div className="category-icon">{icon}</div>
                              <h3 className="category-name">
                                {category.replace('-', ' ')}
                              </h3>
                              <div className="category-stats">
                                <span className="product-count">
                                  {stats.count} Products
                                </span>
                              </div>
                            </div>
                            
                            <div className="card-back">
                              <div className="back-content">
                                <span className="explore-text">Explore</span>
                                <span className="category-emoji">{icon}</span>
                                <span className="avg-price">
                                  From ${stats.avgPrice}
                                </span>
                              </div>
                            </div>
                          </div>
                        </button>
                      );
                    })}
                </div>
              ))}
            </div>
          </div>

          {/* Carousel Dots */}
          {totalSlides > 1 && (
            <div className="carousel-dots">
              {Array.from({ length: totalSlides }).map((_, index) => (
                <button
                  key={index}
                  className={`carousel-dot ${index === currentSlide ? 'active' : ''}`}
                  onClick={() => goToSlide(index)}
                  aria-label={`Go to slide ${index + 1}`}
                />
              ))}
            </div>
          )}
        </div>
      </section>

      {/* Benefits Section */}
      <section className="section benefits-section">
        <div className="container">
          <div className="section-header centered">
            <span className="section-tag">Why Choose Us</span>
            <h2 className="section-title">The Designer DC Difference</h2>
            <p className="section-subtitle">Experience premium service with every order</p>
          </div>

          <div className="benefits-grid">
            <div className="benefit-card" data-aos="fade-up">
              <div className="benefit-icon-wrapper">
                <div className="benefit-icon">
                  <svg viewBox="0 0 24 24" fill="none">
                    <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm0 18c-4.41 0-8-3.59-8-8s3.59-8 8-8 8 3.59 8 8-3.59 8-8 8zm-.5-13v6l5.25 3.15.75-1.23-4.5-2.67V7z"
                      fill="currentColor"/>
                  </svg>
                </div>
              </div>
              <h3>Lightning Fast Delivery</h3>
              <p>Delivery within 60 minutes across Washington DC with real-time tracking and live updates</p>
              <div className="benefit-stats">
                <span className="stat">⏱️ Avg: 45min</span>
                <span className="stat">📍 DC Metro</span>
              </div>
            </div>

            <div className="benefit-card" data-aos="fade-up" data-aos-delay="100">
              <div className="benefit-icon-wrapper">
                <div className="benefit-icon">
                  <svg viewBox="0 0 24 24" fill="none">
                    <path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z"
                      fill="currentColor"/>
                  </svg>
                </div>
              </div>
              <h3>Premium Quality</h3>
              <p>Every product is lab-tested and verified for potency, purity, and safety</p>
              <div className="benefit-stats">
                <span className="stat">🔬 Lab Tested</span>
                <span className="stat">⭐ Top Rated</span>
              </div>
            </div>

            <div className="benefit-card" data-aos="fade-up" data-aos-delay="200">
              <div className="benefit-icon-wrapper">
                <div className="benefit-icon">
                  <svg viewBox="0 0 24 24" fill="none">
                    <path d="M18 8h-1V6c0-2.76-2.24-5-5-5S7 3.24 7 6v2H6c-1.1 0-2 .9-2 2v10c0 1.1.9 2 2 2h12c1.1 0 2-.9 2-2V10c0-1.1-.9-2-2-2zm-6 9c-1.1 0-2-.9-2-2s.9-2 2-2 2 .9 2 2-.9 2-2 2zm3.1-9H8.9V6c0-1.71 1.39-3.1 3.1-3.1 1.71 0 3.1 1.39 3.1 3.1v2z"
                      fill="currentColor"/>
                  </svg>
                </div>
              </div>
              <h3>Secure & Discreet</h3>
              <p>Professional delivery with unmarked vehicles and discreet packaging</p>
              <div className="benefit-stats">
                <span className="stat">🔒 100% Secure</span>
                <span className="stat">📦 Discreet</span>
              </div>
            </div>

            <div className="benefit-card" data-aos="fade-up" data-aos-delay="300">
              <div className="benefit-icon-wrapper">
                <div className="benefit-icon">
                  <svg viewBox="0 0 24 24" fill="none">
                    <path d="M16 11c1.66 0 2.99-1.34 2.99-3S17.66 5 16 5c-1.66 0-3 1.34-3 3s1.34 3 3 3zm-8 0c1.66 0 2.99-1.34 2.99-3S9.66 5 8 5C6.34 5 5 6.34 5 8s1.34 3 3 3zm0 2c-2.33 0-7 1.17-7 3.5V19h14v-2.5c0-2.33-4.67-3.5-7-3.5zm8 0c-.29 0-.62.02-1 .05 1.16.84 2 1.87 2 3.45V19h6v-2.5c0-2.33-4.67-3.5-7-3.5z"
                      fill="currentColor"/>
                  </svg>
                </div>
              </div>
              <h3>Expert Staff</h3>
              <p>Knowledgeable team ready to help you find the perfect products</p>
              <div className="benefit-stats">
                <span className="stat">🎓 Certified</span>
                <span className="stat">💬 24/7 Support</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="section cta-section">
        <div className="container">
          <div className="cta-content">
            <h2 className="cta-title">Ready to Experience Premium Cannabis?</h2>
            <p className="cta-text">Join thousands of satisfied customers in Washington DC</p>
            <div className="cta-actions">
              <button
                onClick={() => handleNavigation('/products')}
                className="btn btn-primary  btn-large"
              >
                Start Shopping
              </button>
              <button
                onClick={() => handleNavigation('/about')}
                className="btn btn-outline-light btn-large"
              >
                Learn More
              </button>
            </div>
          </div>
        </div>
      </section>

      <style>{`
        /* Hero Section Responsive Styles */
        .hero-section {
          min-height: clamp(500px, 80vh, 700px);
          display: flex;
          align-items: center;
          position: relative;
          color: white;
          padding: var(--space-3xl) 0;
        }

        .hero-content {
          max-width: 700px;
          position: relative;
          z-index: 2;
          animation: fadeInUp 1s ease;
        }

        .hero-badge {
          display: inline-flex;
          align-items: center;
          gap: 8px;
          background: rgba(255, 255, 255, 0.2);
          backdrop-filter: blur(10px);
          padding: 8px 16px;
          border-radius: 40px;
          font-size: clamp(0.8rem, 2vw, 0.9rem);
          margin-bottom: 24px;
          border: 1px solid rgba(255, 255, 255, 0.3);
        }

        .badge-icon {
          font-size: 1.1rem;
        }

        .hero-title {
          font-size: clamp(2rem, 8vw, 4rem);
          font-weight: 800;
          line-height: 1.1;
          margin-bottom: 20px;
        }

        .title-accent {
          display: block;
          color: var(--accent);
          font-size: clamp(2.5rem, 10vw, 5rem);
        }

        .hero-subtitle {
          font-size: clamp(1rem, 3vw, 1.25rem);
          line-height: 1.6;
          margin-bottom: 32px;
          opacity: 0.9;
          max-width: 600px;
        }

        .hero-actions {
          display: flex;
          flex-wrap: wrap;
          gap: 16px;
          margin-bottom: 48px;
        }

        .hero-stats {
          display: flex;
          flex-wrap: wrap;
          gap: clamp(20px, 5vw, 40px);
          margin-top: 32px;
        }

        .stat-item {
          display: flex;
          flex-direction: column;
        }

        .stat-number {
          font-size: clamp(1.5rem, 4vw, 2rem);
          font-weight: 700;
          color: var(--accent);
        }

        .stat-label {
          font-size: 0.9rem;
          opacity: 0.8;
        }

        .hero-wave {
          position: absolute;
          bottom: 0;
          left: 0;
          right: 0;
          line-height: 0;
        }

        .hero-wave svg {
          width: 100%;
          height: auto;
        }

        /* Section Headers */
        .section-header {
          display: flex;
          flex-wrap: wrap;
          align-items: flex-end;
          justify-content: space-between;
          margin-bottom: clamp(30px, 5vw, 50px);
          gap: 20px;
        }

        .section-header.centered {
          text-align: center;
          justify-content: center;
        }

        .section-tag {
          display: inline-block;
          font-size: 0.9rem;
          font-weight: 600;
          color: var(--primary);
          text-transform: uppercase;
          letter-spacing: 2px;
          margin-bottom: 8px;
        }

        .section-title {
          font-size: clamp(1.8rem, 5vw, 2.5rem);
          font-weight: 700;
          color: var(--text);
          margin-bottom: 8px;
        }

        .section-subtitle {
          font-size: clamp(1rem, 2.5vw, 1.1rem);
          color: var(--text-light);
        }

        /* Products Grid */
        .products-grid {
          display: grid;
          grid-template-columns: repeat(auto-fill, minmax(min(100%, 280px), 1fr));
          gap: clamp(16px, 3vw, 30px);
        }

        /* Category Cards */
        .category-card {
          position: relative;
          width: 100%;
          height: 180px;
          perspective: 1500px;
          cursor: pointer;
          border: none;
          background: none;
          padding: 0;
        }

        .card-inner {
          position: relative;
          width: 100%;
          height: 100%;
          text-align: center;
          transition: transform 0.6s;
          transform-style: preserve-3d;
          border-radius: var(--radius-lg);
        }

        .category-card:hover .card-inner {
          transform: rotateY(180deg);
        }

        .card-front, .card-back {
          position: absolute;
          width: 100%;
          height: 100%;
          backface-visibility: hidden;
          border-radius: var(--radius-lg);
          padding: 20px;
          display: flex;
          flex-direction: column;
          align-items: center;
          justify-content: center;
        }

        .card-front {
          background: white;
          box-shadow: var(--shadow-lg);
        }

        .card-back {
          background: linear-gradient(135deg, var(--primary) 0%, var(--primary-dark) 100%);
          color: white;
          transform: rotateY(180deg);
        }

        .category-icon {
          font-size: clamp(2rem, 5vw, 2.5rem);
          margin-bottom: 12px;
        }

        .category-name {
          font-size: clamp(1rem, 2.5vw, 1.2rem);
          font-weight: 600;
          color: var(--text);
          margin-bottom: 8px;
        }

        .category-stats {
          font-size: 0.9rem;
          color: var(--text-light);
        }

        .back-content {
          display: flex;
          flex-direction: column;
          align-items: center;
          gap: 8px;
        }

        .explore-text {
          font-size: 1rem;
          font-weight: 600;
        }

        .category-emoji {
          font-size: 2rem;
        }

        .avg-price {
          font-size: 0.9rem;
          opacity: 0.9;
        }

        /* Carousel */
        .category-carousel-container {
          overflow: hidden;
          position: relative;
          margin: 0 -10px;
        }

        .carousel-track {
          display: flex;
          transition: transform 0.5s ease;
        }

        .carousel-slide {
          flex: 0 0 100%;
          display: grid;
          grid-template-columns: repeat(${slidesPerView}, 1fr);
          gap: clamp(16px, 3vw, 24px);
          padding: 0 10px;
        }

        .carousel-controls {
          display: flex;
          gap: 12px;
        }

        .carousel-btn {
          width: 44px;
          height: 44px;
          border-radius: 50%;
          border: 2px solid var(--border);
          background: white;
          color: var(--text);
          cursor: pointer;
          transition: all 0.3s ease;
          display: flex;
          align-items: center;
          justify-content: center;
        }

        .carousel-btn:hover:not(:disabled) {
          background: var(--primary);
          border-color: var(--primary);
          color: white;
          transform: scale(1.1);
        }

        .carousel-btn:disabled {
          opacity: 0.5;
          cursor: not-allowed;
        }

        .carousel-dots {
          display: flex;
          justify-content: center;
          gap: 12px;
          margin-top: 32px;
        }

        .carousel-dot {
          width: 10px;
          height: 10px;
          border-radius: 50%;
          border: 2px solid var(--border);
          background: transparent;
          cursor: pointer;
          transition: all 0.3s ease;
          padding: 0;
        }

        .carousel-dot.active {
          background: var(--primary);
          border-color: var(--primary);
          transform: scale(1.3);
        }

        /* Benefits Grid */
        .benefits-grid {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(min(100%, 250px), 1fr));
          gap: clamp(20px, 4vw, 30px);
          margin-top: 40px;
        }

        .benefit-card {
          background: white;
          border-radius: var(--radius-lg);
          padding: clamp(20px, 4vw, 30px);
          box-shadow: var(--shadow-lg);
          transition: all 0.3s ease;
          text-align: center;
        }

        .benefit-card:hover {
          transform: translateY(-10px);
          box-shadow: var(--shadow-2xl);
        }

        .benefit-icon-wrapper {
          display: flex;
          justify-content: center;
          margin-bottom: 20px;
        }

        .benefit-icon {
          width: 80px;
          height: 80px;
          border-radius: 50%;
          background: linear-gradient(135deg, rgba(5, 150, 105, 0.1) 0%, rgba(16, 185, 129, 0.1) 100%);
          display: flex;
          align-items: center;
          justify-content: center;
          color: var(--primary);
          font-size: 2rem;
        }

        .benefit-card h3 {
          font-size: clamp(1.2rem, 3vw, 1.4rem);
          margin-bottom: 12px;
          color: var(--text);
        }

        .benefit-card p {
          color: var(--text-light);
          line-height: 1.6;
          margin-bottom: 16px;
        }

        .benefit-stats {
          display: flex;
          flex-wrap: wrap;
          justify-content: center;
          gap: 12px;
        }

        .benefit-stats .stat {
          font-size: 0.85rem;
          padding: 4px 12px;
          background: rgba(5, 150, 105, 0.1);
          border-radius: 20px;
          color: var(--primary-dark);
        }

        /* CTA Section */
        .cta-section {
          background: linear-gradient(135deg, var(--primary) 0%, var(--primary-dark) 100%);
          padding: clamp(40px, 8vw, 80px) 0;
          margin-top: 40px;
          text-align: center;
          color: white;
        }

        .cta-content {
          max-width: 700px;
          margin: 0 auto;
        }

        .cta-title {
          font-size: clamp(1.8rem, 5vw, 2.8rem);
          font-weight: 700;
          margin-bottom: 16px;
        }

        .cta-text {
          font-size: clamp(1rem, 2.5vw, 1.2rem);
          opacity: 0.9;
          margin-bottom: 32px;
        }

        .cta-actions {
          display: flex;
          flex-wrap: wrap;
          justify-content: center;
          gap: 16px;
        }

        .btn {
          display: inline-flex;
          align-items: center;
          gap: 8px;
          padding: clamp(10px, 2vw, 16px) clamp(20px, 4vw, 32px);
          font-size: clamp(0.9rem, 2vw, 1rem);
          font-weight: 600;
          border-radius: var(--radius-md);
          border: 2px solid transparent;
          cursor: pointer;
          transition: all 0.3s ease;
          text-decoration: none;
          text-align: center;
          justify-content: center;
        }

        .btn-primary {
          background: white;
          color: var(--primary);
        }

        .btn-primary:hover {
        background: transparent;
          box-shadow: 0 10px 25px rgba(0, 0, 0, 0.2);
        }

        .btn-outline-light {
          background: transparent;
          border-color: white;
          color: white;
        }

        .btn-outline-light:hover {
          background: white;
          color: var(--primary);
        }

        .btn-large {
          padding: clamp(12px, 2.5vw, 18px) clamp(24px, 5vw, 40px);
          font-size: clamp(1rem, 2.2vw, 1.1rem);
        }

        .arrow-icon {
          width: 18px;
          height: 18px;
        }

        /* Animations */
        @keyframes fadeInUp {
          from {
            opacity: 0;
            transform: translateY(30px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }

        /* Mobile Responsive Adjustments */
        @media (max-width: 768px) {
          .hero-actions {
            flex-direction: column;
          }
          
          .hero-actions .btn {
            width: 100%;
          }
          
          .carousel-slide {
            grid-template-columns: repeat(${Math.min(slidesPerView, 2)}, 1fr);
          }
          
          .section-header {
            flex-direction: column;
            align-items: flex-start;
          }
          
          .carousel-controls {
            align-self: flex-end;
          }
          
          .benefits-grid {
            grid-template-columns: repeat(auto-fit, minmax(min(100%, 220px), 1fr));
          }
        }

        @media (max-width: 480px) {
          .carousel-slide {
            grid-template-columns: 1fr;
          }
          
          .hero-stats {
            flex-direction: column;
            gap: 16px;
          }
          
          .cta-actions {
            flex-direction: column;
          }
          
          .btn-large {
            width: 100%;
          }
          
          .category-card {
            height: 160px;
          }
        }

        /* Touch Device Optimization */
        @media (hover: none) {
          .category-card:hover .card-inner {
            transform: none;
          }
          
          .benefit-card:hover {
            transform: none;
          }
        }

        /* Dark Mode Support */
        @media (prefers-color-scheme: dark) {
          .card-front {
            background: #2d2d2d;
          }
          
          .category-name {
            color: #fff;
          }
          
          .benefit-card {
            background: #2d2d2d;
          }
          
          .benefit-card h3 {
            color: #fff;
          }
        }
      `}</style>
    </div>
  );
};

export default HomePage;